import { router } from "@inertiajs/react";
import {
  ArrowDown,
  ArrowUp,
  CirclePlus,
  Loader2,
  Trash2,
  Users,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject } from "react";

import AsyncCombobox, { type AsyncOption } from "@/components/async-combobox";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { normalizeCurrencyValue } from "@/helpers/currency-formatter";
import { useOrganizationRoutes } from "@/hooks/use-organization-routes";
import type { ApprovalAssigneeSummary, ApprovalWorkflowConfig } from "@/types";

interface ApprovalWorkflowManagerProps {
  sectorId: number;
  workflow: ApprovalWorkflowConfig | null;
}

interface LevelState {
  id?: number;
  clientId: string;
  users: UserState[];
  minimumAmount?: number | null;
}

interface UserState {
  id: number;
  fullName?: string | null;
  email: string;
}

interface SerializableLevel {
  id?: number;
  minimum_amount?: number | null;
  user_ids: number[];
}

function generateClientId() {
  return `level-${Math.random().toString(36).slice(2, 10)}`;
}

function mapWorkflowToState(
  workflow: ApprovalWorkflowConfig | null,
): LevelState[] {
  if (!workflow) return [];
  return workflow.levels
    .slice()
    .sort((a, b) => a.position - b.position)
    .map((level) => ({
      id: level.id,
      clientId: `existing-${level.id}`,
      minimumAmount: normalizeCurrencyValue(level.minimum_amount),
      users: level.users.map((user) => ({
        id: user.id,
        fullName: user.full_name,
        email: user.email,
      })),
    }));
}

function serialize(levels: LevelState[]): SerializableLevel[] {
  return levels.map((level) => ({
    id: level.id,
    minimum_amount: level.minimumAmount,
    user_ids: level.users.map((user) => user.id),
  }));
}

export default function ApprovalWorkflowManager({
  sectorId,
  workflow,
}: ApprovalWorkflowManagerProps) {
  const [levels, setLevels] = useState<LevelState[]>(() =>
    mapWorkflowToState(workflow),
  );
  const [saving, setSaving] = useState(false);
  const routes = useOrganizationRoutes();
  const organizationSlug = routes.organizationSlug;

  useEffect(() => {
    setLevels(mapWorkflowToState(workflow));
  }, [workflow]);

  const initialSignature = useMemo(
    () => JSON.stringify(serialize(mapWorkflowToState(workflow))),
    [workflow],
  );
  const currentSignature = useMemo(
    () => JSON.stringify(serialize(levels)),
    [levels],
  );
  const isDirty = initialSignature !== currentSignature;

  const addLevel = () => {
    setLevels((prev) => [...prev, { clientId: generateClientId(), users: [] }]);
  };

  const removeLevel = (clientId: string) => {
    setLevels((prev) => prev.filter((level) => level.clientId !== clientId));
  };

  const moveLevel = (from: number, to: number) => {
    setLevels((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const addUserToLevel = (clientId: string, user: UserState) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.clientId !== clientId) return level;
        if (level.users.some((existing) => existing.id === user.id))
          return level;
        return { ...level, users: [...level.users, user] };
      }),
    );
  };

  const removeUserFromLevel = (clientId: string, userId: number) => {
    setLevels((prev) =>
      prev.map((level) => {
        if (level.clientId !== clientId) return level;
        return {
          ...level,
          users: level.users.filter((user) => user.id !== userId),
        };
      }),
    );
  };

  const updateLevelAmount = (clientId: string, amount: number | null) => {
    setLevels((prev) =>
      prev.map((level) =>
        level.clientId === clientId
          ? { ...level, minimumAmount: amount }
          : level,
      ),
    );
  };

  const optionsCache = useRef(new Map<number, ApprovalAssigneeSummary>());

  const loadOptions = useCallback(
    async (term: string): Promise<AsyncOption[]> => {
      if (!organizationSlug) return [];

      const params = new URLSearchParams();
      if (term.trim()) params.set("term", term.trim());

      const response = await fetch(
        `${routes.members.search()}?${params.toString()}`,
      );
      if (!response.ok) return [];

      const data = (await response.json()) as ApprovalAssigneeSummary[];
      data.forEach((entry) => optionsCache.current.set(entry.id, entry));

      return data.map((entry) => ({
        value: entry.id,
        label: entry.full_name
          ? `${entry.full_name} (${entry.email})`
          : entry.email,
      }));
    },
    [organizationSlug],
  );

  const handleSave = () => {
    setSaving(true);
    const payload = {
      active: levels.length > 0,
      levels: levels.map((level, index) => ({
        id: level.id,
        position: index,
        minimum_amount: level.minimumAmount ?? null,
        user_ids: level.users.map((user) => user.id),
      })),
    };

    router.put(
      routes.sectors.approvalWorkflow(sectorId),
      { approval_workflow: payload },
      {
        preserveScroll: true,
        preserveState: false,
        onFinish: () => setSaving(false),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Niveles de aprobación
        </CardTitle>
        <CardDescription>
          Define los pasos y asigna quién puede aprobar en cada uno. Se guardan
          en el orden mostrado.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {levels.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            Aún no hay niveles configurados. Agregá el primero para comenzar.
          </div>
        ) : (
          <div className="space-y-4">
            {levels.map((level, index) => (
              <LevelRow
                key={level.clientId}
                level={level}
                index={index}
                total={levels.length}
                onMove={moveLevel}
                onRemove={removeLevel}
                onAddUser={addUserToLevel}
                onRemoveUser={removeUserFromLevel}
                onUpdateAmount={updateLevelAmount}
                loadOptions={loadOptions}
                optionsCache={optionsCache}
              />
            ))}
          </div>
        )}

        <Button
          type="button"
          variant="outline"
          onClick={addLevel}
          className="gap-2"
        >
          <CirclePlus className="h-4 w-4" /> Agregar nivel
        </Button>
      </CardContent>
      <Separator />
      <CardFooter className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {levels.length} nivel{levels.length === 1 ? "" : "es"} configurado
          {levels.length === 1 ? "" : "s"}
        </span>
        <Button
          type="button"
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="gap-2"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Guardar niveles de aprobación
        </Button>
      </CardFooter>
    </Card>
  );
}

interface LevelRowProps {
  level: LevelState;
  index: number;
  total: number;
  onMove: (from: number, to: number) => void;
  onRemove: (clientId: string) => void;
  onAddUser: (clientId: string, user: UserState) => void;
  onRemoveUser: (clientId: string, userId: number) => void;
  onUpdateAmount: (clientId: string, amount: number | null) => void;
  loadOptions: (term: string) => Promise<AsyncOption[]>;
  optionsCache: MutableRefObject<Map<number, ApprovalAssigneeSummary>>;
}

function LevelRow({
  level,
  index,
  total,
  onMove,
  onRemove,
  onAddUser,
  onRemoveUser,
  onUpdateAmount,
  loadOptions,
  optionsCache,
}: LevelRowProps) {
  const [pendingOption, setPendingOption] = useState<AsyncOption | null>(null);

  const handleAdd = (option: AsyncOption | null) => {
    if (!option) return;
    const userId = Number(option.value);
    const user = optionsCache.current.get(userId);
    if (!user) return;

    onAddUser(level.clientId, {
      id: user.id,
      fullName: user.full_name,
      email: user.email,
    });
    setPendingOption(null);
  };

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="text-sm font-semibold">Nivel {index + 1}</p>
          <p className="text-xs text-muted-foreground">
            Las personas listadas deben decidir antes de pasar al siguiente
            nivel.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start">
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={index === 0}
            onClick={() => onMove(index, index - 1)}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={index === total - 1}
            onClick={() => onMove(index, index + 1)}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onRemove(level.clientId)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {level.users.length === 0 ? (
          <p className="rounded border border-dashed p-3 text-sm text-muted-foreground">
            Todavía no asignaste usuarios a este nivel.
          </p>
        ) : (
          <ul className="space-y-2">
            {level.users.map((user) => (
              <li
                key={user.id}
                className="flex items-center justify-between rounded border bg-muted/40 px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-medium">
                    {user.fullName ?? user.email}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {user.email}
                  </span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => onRemoveUser(level.clientId, user.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Agregar persona
          </p>
          <AsyncCombobox
            value={pendingOption}
            onChange={(option) => {
              setPendingOption(option);
              handleAdd(option);
            }}
            loadOptions={loadOptions}
            defaultOptions={true}
            cacheOptions={true}
            placeholder="Buscar persona..."
          />
        </div>

        <div className="space-y-2">
          <Label
            htmlFor={`amount-${level.clientId}`}
            className="text-xs font-medium text-muted-foreground"
          >
            Monto mínimo (ARS)
          </Label>
          <Input
            id={`amount-${level.clientId}`}
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={level.minimumAmount ?? ""}
            onChange={(e) => {
              onUpdateAmount(
                level.clientId,
                normalizeCurrencyValue(e.target.value),
              );
            }}
          />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Si el monto de la solicitud es mayor a este valor, requerirá aprobación
        manual. Dejar vacío para requerir siempre.
      </p>
    </div>
  );
}
